import React, { useMemo } from 'react';
import { formatDate } from '../../utils/date';
import { formatDni } from '../../utils/formatters';
import DodoHeader from './DodoHeader';
import logoOrange from '../../images/logoOrange.png';
import './File.css';
import './Receipt.css';

const money = (currency, value) => {
  const c = currency || '$';
  const v = Number(value || 0);
  // Similar al ejemplo: "USD 1950" (sin separadores cuando es entero)
  const isInt = Number.isFinite(v) && Math.abs(v - Math.round(v)) < 1e-9;
  return `${c} ${v.toLocaleString('es-AR', { minimumFractionDigits: isInt ? 0 : 2, maximumFractionDigits: 2 })}`;
};

const normalizePaymentType = (type) => {
  const t = String(type || '').toLowerCase();
  if (!t) return '-';
  if (t === 'tranferencia') return 'Transferencia';
  if (t === 'transferencia') return 'Transferencia';
  if (t === 'efectivo') return 'Efectivo';
  if (t === 'credito') return 'Crédito';
  return type;
};

const PrintReceipt = ({ file, payments = [] }) => {
  const newestPayment = useMemo(() => {
    if (!payments.length) return null;
    return [...payments].sort((a, b) => {
      const aTime = Date.parse(a?.date || '');
      const bTime = Date.parse(b?.date || '');
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) return bTime - aTime;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    })[0];
  }, [payments]);

  const receiptNumber = useMemo(() => {
    const n = Number(newestPayment?.id);
    if (!Number.isFinite(n)) return '-';
    return String(Math.trunc(n)).padStart(5, '0');
  }, [newestPayment]);

  const passengerCount = useMemo(() => {
    const fromApi = file?.passengers || file?.passengerList || file?.passenger_list || [];
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi.length;
    return 1;
  }, [file]);

  const passengerLabel = useMemo(() => {
    const surname = (file?.client_surname || '').toString().trim();
    const name = (file?.client_name || '').toString().trim();
    const base = [surname, name].filter(Boolean).join(' / ') || '-';
    return passengerCount > 1 ? `${base} X${passengerCount}`.toUpperCase() : base.toUpperCase();
  }, [file, passengerCount]);

  const receiptFrom = useMemo(() => {
    const surname = (file?.client_surname || '').toString().trim();
    const name = (file?.client_name || '').toString().trim();
    const base = [surname, name].filter(Boolean).join(' / ') || '-';
    return base.toUpperCase();
  }, [file]);

  const rows = useMemo(() => {
    return payments.map((payment) => {
      const detail = (payment?.detail || '').toString().trim();
      return {
        id: payment?.id,
        type: normalizePaymentType(payment?.type).toString().toUpperCase(),
        detail: detail ? detail.toUpperCase() : '',
        amount: money(payment?.currency, payment?.value).toUpperCase()
      };
    });
  }, [payments]);

  return (
    <div className="print-a4">
      <div className="print-header receipt-header">
        <DodoHeader />

        <div className="print-header-right receipt-header-right">
          <div className="receipt-header-meta">
            <div className="receipt-title">RECIBO</div>
            <div className="receipt-meta">CÓRDOBA-ARGENTINA</div>
            <div className="receipt-meta">RECIBO N° {receiptNumber}</div>
            <div className="receipt-meta">FECHA: {formatDate(newestPayment?.date)}</div>
          </div>

          <div className="receipt-top-right">
            <div className="receipt-x">X</div>
            <div className="receipt-invalid">
              DOCUMENTO NO
              <br />
              VÁLIDO
              <br />
              COMO FACTURA
            </div>
          </div>
        </div>
      </div>

      <div className="receipt-kv">
        <div className="receipt-kv-item">
          <span className="receipt-label">RESERVA N°:</span>
          <span className="receipt-value">{file?.id ?? '-'}</span>
        </div>
        <div className="receipt-kv-item">
          <span className="receipt-label">A NOMBRE DE:</span>
          <span className="receipt-value">{receiptFrom}</span>
        </div>

        <div className="receipt-kv-item">
          <span className="receipt-label">DNI:</span>
          <span className="receipt-value">{formatDni(file?.client_dni) || '-'}</span>
        </div>

        <div className="receipt-kv-item">
          <span className="receipt-label">IVA:</span>
          <span className="receipt-value">CONSUMIDOR FINAL</span>
        </div>
      </div>

      <div className="receipt-paragraph">
        En concepto de reserva de los servicios contratados a requerir. Si el monto del servicio es en moneda
        extranjera y usted abona en pesos, el tipo de cambio será tentativo a la fecha de pago, pudiendo
        modificarse en las próximas 48hs hábiles, cuya diferencia se aplicará en este recibo.
      </div>

      <table className="receipt-table">
        <thead>
          <tr>
            <th style={{ width: '28%' }}>PASAJERO</th>
            <th style={{ width: '22%' }}>MEDIO DE PAGO</th>
            <th>DETALLE</th>
            <th style={{ width: '16%', textAlign: 'right' }}>IMPORTE</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{passengerLabel}</td>
              <td>{row.type}</td>
              <td>{row.detail}</td>
              <td style={{ textAlign: 'right' }}>{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="receipt-footer">

        <span>
          DODO TRAVEL ACTÚA COMO INTERMEDIARIO ENTRE LOS PASAJEROS Y LOS DISTINTOS PROVEEDORES QUE PRESTAN LOS
          SERVICIOS CONTRATADOS, COMO AÉREOS, ALOJAMIENTOS, TRASLADOS, EXCURSIONES, ETC. HABIENDO EFECTUADO ESTE
          RECIBO A NOMBRE PROPIO POR CUENTA Y ORDEN DEL PRESTADOR DEL SERVICIO.
        </span>
      </div>
    </div>
  );
};

export default PrintReceipt;

